import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangleIcon } from 'lucide-react';
import { ErrorCode, adminLoginRequestSchema, adminSessionSchema } from '@qaytim/shared';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/field';
import { Input } from '@/components/ui/input';
import { AdminApiError, adminRequest } from '@/lib/admin-api';
import { authStore } from '@/lib/auth-store';
import { formatTashkentTime } from '@/lib/time';
import { strings } from '@/i18n/strings';

/**
 * Admin login (§4.13: a separate auth realm from Telegram user auth).
 *
 * Endpoint P3.5 da paydo bo'ldi va forma allaqachon yakuniy kelishuvda yozilgani uchun
 * (`adminLoginRequestSchema` / `adminSessionSchema`) ulash uchun bu yerda hech narsa
 * o'zgartirilmadi.
 *
 * Server topilmagan email, noto'g'ri parol va o'chirilgan hisobni bir xil kod bilan qaytaradi —
 * forma ham ularni ajratmaydi, ya'ni bu yerdagi "hammasi bitta jumla" mantiqi soddalik emas,
 * balki serverdagi qarorning davomi.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = adminLoginRequestSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(email.trim() === '' ? strings.login.emailRequired : strings.login.passwordRequired);
      return;
    }

    setSubmitting(true);
    try {
      const session = await adminRequest('/admin/auth/login', {
        method: 'POST',
        body: parsed.data,
        schema: adminSessionSchema,
      });
      authStore.set(session);
      await navigate({ to: '/' });
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.code === 'NETWORK_ERROR') {
        setError(strings.login.networkError);
      } else if (cause instanceof AdminApiError && cause.code === ErrorCode.RATE_LIMITED) {
        /**
         * Chegaraga urilgan urinishni "parol noto'g'ri" deb ko'rsatib bo'lmaydi.
         *
         * Efirdan yigirma daqiqa oldin ikki operator bitta hisobga kirishga urinsa, o'n birinchi
         * urinishda server 429 qaytaradi. "Email yoki parol noto'g'ri" degan javob ularni parolni
         * qidirishga yuboradi — aslida esa kutish kerak, va server aynan qachongacha kutish
         * kerakligini (`retryAt`) yuborib turibdi.
         */
        const retryAt = cause.details?.retryAt;
        setError(
          typeof retryAt === 'string'
            ? strings.login.rateLimited.replace('{time}', formatTashkentTime(retryAt))
            : strings.drawErrors.rateLimited,
        );
      } else {
        setError(strings.login.invalidCredentials);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-canvas flex min-h-screen items-center justify-center px-4 py-10">
      {/*
        Fon dog'lari — mazmunsiz va shunday bo'lishi kerak: ular ostidagi rangdan boshqa hech
        narsa bermaydi. Ekran o'quvchisi uchun bu ekranda faqat forma bor.
      */}
      <div aria-hidden="true" className="auth-orb auth-orb--brand" />
      <div aria-hidden="true" className="auth-orb auth-orb--cool" />

      <Card className="motion-rise bg-card/85 relative z-10 w-full max-w-sm shadow-(--shadow-3) backdrop-blur-xl">
        <CardHeader className="items-center text-center">
          <span
            aria-hidden="true"
            className="from-primary to-primary/70 text-primary-foreground shadow-[0_6px_24px_-6px_var(--primary)] ring-primary/15 mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br text-xl font-black ring-8"
          >
            Q
          </span>
          <CardTitle className="text-lg">{strings.login.title}</CardTitle>
          <CardDescription>{strings.app.title}</CardDescription>
        </CardHeader>

        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <Field label={strings.login.email}>
              <Input
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field label={strings.login.password}>
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {error ? (
              <Alert variant="destructive" role="alert" className="motion-rise">
                <AlertTriangleIcon aria-hidden="true" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <Button type="submit" loading={submitting} className="w-full">
              {submitting ? strings.login.submitting : strings.login.submit}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
